import { useRef } from 'react';
import {
  Box,
  Table,
  Pagination,
  TextInput,
  Button,
  MediaQuery,
  Modal,
  Stack,
  Grid,
  Col,
  Radio,
  Badge,
  Text,
  Divider,
  Select,
  Group,
  Checkbox,
  MultiSelect
} from '@mantine/core';
import { BiDollar } from 'react-icons/bi';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';

import { useStyles } from './style';
import edit from '../../../assets/icons/edit.svg';
import view from '../../../assets/icons/view.svg';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SearchIcon } from '../../../assets/icons/search';
import { data, statusClass, propertiesUserType } from './properties';
import { baseURL } from '../../../config/urls';
import {
  getHeader,
  doRedirectIfUnAuth,
  getUserType
} from '../../../config/userSession';
import plusIcon from '../../../assets/icons/plus-icon.svg';
import { IconCheck, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { noRecordFound } from '../../../utils/const';
import { useLocation } from 'react-router-dom';
import handleSearch from '../Search';

function PropertyManagementList() {
  const userType = getUserType();
  const [viewAddManager, setViewAddManager] = useState(false);
  const [oldProperty, setOldproperty] = useState('');
  const [editManager, setEditManager] = useState(false);
  const [sending, setSending] = useState(false);
  const { classes, theme } = useStyles(useStyles);
  const [activePage, setActivePage] = useState(1);
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [properties, setProperties] = useState([]);
  const [pageLoadEntries, setPageLoadEntries] = useState([]);
  const [totalPage, setTotalPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ✅ Separate cache refs for different endpoints
  const cacheManagersListing = useRef({});
  const cachePropertiesListing = useRef({});

  const [hasManagerEmail, setHasManagerEmail] = useState(false);

  const mangerform = useForm({
    initialValues: {
      propertyManagerName: '',
      propertyManagerEmail: '',
      propertyManagerAccess: null,
      propertyIds: []
    },
    validate: {
      propertyManagerName: (val) => (val.length < 2 ? 'Name too short' : null),
      propertyManagerEmail: (val) =>
        /^\S+@\S+$/.test(val) ? null : 'Invalid email address',
      propertyIds: (val) =>
        !val || val.length === 0 ? 'Select at least one property' : null
    }
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');

  const pageChangeHandler = (page) => {
    setActivePage(page);
  };

  const fetchContactForm = async () => {
    // ✅ Create params inside function to avoid reuse
    const params = new URLSearchParams();
    params.append('page', activePage);
    params.append('pSize', pageSize);
    if (email) params.append('email', email);

    const cacheKey = params.toString();

    // ✅ Check managers cache
    if (cacheManagersListing.current[cacheKey]) {
      setEntries(cacheManagersListing.current[cacheKey]);
      setPageLoadEntries(cacheManagersListing.current[cacheKey]);
      return;
    }

    try {
      const response = await fetch(
        `${baseURL}/property/property-managers-list?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeader()
        }
      );

      if (response.ok) {
        let contactFormListing = await response.json();

        setEntries(contactFormListing.data);
        setPageLoadEntries(contactFormListing.data);
        setTotalPage(Math.ceil(contactFormListing.totalCount / pageSize));

        // ✅ Cache in managers ref
        cacheManagersListing.current[cacheKey] = contactFormListing.data;
      } else {
        doRedirectIfUnAuth(response.status);
      }
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const fetchAllProperties = async () => {
    // ✅ Create separate params for properties
    const params = new URLSearchParams();
    params.append('page', 1); // Always fetch first page
    params.append('pSize', 1000); // Fetch all for dropdown
    if (email) params.append('email', email);

    const cacheKey = params.toString();

    // ✅ Check properties cache
    if (cachePropertiesListing.current[cacheKey]) {
      setProperties(cachePropertiesListing.current[cacheKey]);
      return;
    }

    try {
      const response = await fetch(
        `${baseURL}/property/list?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeader()
        }
      );

      if (response.ok) {
        let propertiesListing = await response.json();
        setProperties(propertiesListing.data);

        // ✅ Cache in properties ref
        cachePropertiesListing.current[cacheKey] = propertiesListing.data;
      } else {
        doRedirectIfUnAuth(response.status);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  // ✅ Separate useEffect for managers (refetch on page change)
  useEffect(() => {
    fetchContactForm();
  }, [activePage, pageSize]);

  // ✅ Separate useEffect for properties (fetch once)
  useEffect(() => {
    fetchAllProperties();
  }, []);

  const renderAccessBadges = (accessPermissions) => {
    if (!Array.isArray(accessPermissions) || accessPermissions.length === 0) {
      return (
        <Text c="dimmed" size="sm">
          No access
        </Text>
      );
    }

    return (
      <Group spacing="xs">
        {accessPermissions.map((access, idx) => (
          <Badge
            key={`${access}-${idx}`}
            variant="light"
            color="blue"
            size="sm"
            radius="sm"
          >
            {formatAccessLabel(access)}
          </Badge>
        ))}
      </Group>
    );
  };

  const handleAddManager = async () => {
    if (!mangerform.isValid()) return;

    const propertyIds = mangerform.getInputProps('propertyIds').value;
    setSending(true);

    const body = {
      ...mangerform.values,
      propertyIds
    };

    if (editManager && oldProperty) {
      body.oldPropertyIds = [oldProperty];
    }

    try {
      const response = await fetch(`${baseURL}/property/manager/batch`, {
        method: 'PUT',
        headers: getHeader(),
        body: JSON.stringify(body)
      });

      if (response.status === 201 || response.status === 200) {
        const result = await response.json();
        showNotification(
          'success',
          '',
          result.msg ||
            `Property manager ${editManager ? 'updated' : 'added'} successfully to ${propertyIds.length} ${propertyIds.length === 1 ? 'property' : 'properties'}`,
          '450px'
        );

        // ✅ Clear both caches to force refresh
        cacheManagersListing.current = {};
        cachePropertiesListing.current = {};

        setViewAddManager(false);

        // ✅ Refresh data
        await fetchContactForm();
        await fetchAllProperties();
      } else {
        let errorMessage = 'Failed to assign manager';

        try {
          const errorData = await response.json();
          errorMessage =
            errorData.error ||
            errorData.response ||
            errorData.message ||
            errorMessage;
        } catch (parseError) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }

        showNotification('failure', '', errorMessage, '550px');
      }
    } catch (error) {
      console.error('Error assigning manager:', error);
      showNotification(
        'failure',
        '',
        'An error occurred while assigning manager. Please try again.',
        '450px'
      );
    }

    setSending(false);
  };

  useEffect(() => {
    if (!viewAddManager) {
      mangerform.setValues({
        propertyManagerName: '',
        propertyManagerEmail: '',
        propertyManagerAccess: null,
        propertyIds: []
      });
      setEditManager(false);
      setOldproperty('');
    }
  }, [viewAddManager]);

  const formatAccessLabel = (value) => {
    switch (value) {
      case 'service-request':
        return 'Service Request';
      case 'invoice':
        return 'Invoice';
      case 'edit-profile':
        return 'Can Edit Profile';
      case 'rent-payment':
        return 'Rent Payment';
      case 'full-access':
        return 'Full Access';
      case 'Read Only':
        return 'Read Only';
      default:
        return value;
    }
  };

  const rows = entries.map((element, index) => {
    return (
      <tr
        className={index % 2 === 0 ? classes.tableRowWhite : classes.tableRow}
        key={`${element.propertyId}-${element.email}-${index}`}
      >
        <td className={classes.tableData}>{element.propertName}</td>
        <td className={classes.tableData}>{element.name}</td>
        <td className={classes.tableData}>{element.email}</td>
        <td className={classes.tableData}>
          {renderAccessBadges(element?.permissions)}
        </td>
        <td className={classes.tableData}>
          <p
            className={`${classes.status} ${
              element.inviteStatus === 'pending'
                ? classes.pending
                : element.inviteStatus === 'accepted'
                  ? classes.accepted
                  : element.inviteStatus === 'expired'
                    ? classes.accepted
                    : element.inviteStatus === 'cancelled' ||
                        (element.ExpirationDate &&
                          new Date(element.ExpirationDate) < new Date())
                      ? classes.expired
                      : ''
            }`}
          >
            {element.ExpirationDate &&
            new Date(element.ExpirationDate) < new Date() &&
            element.inviteStatus !== 'accepted' &&
            element.inviteStatus !== 'cancelled'
              ? 'expired'
              : element.inviteStatus}
          </p>
        </td>
        <td className={classes.tableData}>
          <Box className={classes.editAndView}>
            <img
              className={classes.action}
              onClick={() => {
                setViewAddManager(true);
                setEditManager(true);
                mangerform.setFieldValue('propertyIds', [element.propertyId]);
                mangerform.setFieldValue(
                  'propertyManagerAccess',
                  element.permissions
                );
                mangerform.setFieldValue('propertyManagerName', element.name);
                mangerform.setFieldValue('propertyManagerEmail', element.email);
                setOldproperty(element.propertyId);
              }}
              style={{ cursor: 'pointer' }}
              src={userType === propertiesUserType.tenant ? view : edit}
              alt="edit"
            />
          </Box>
        </td>
      </tr>
    );
  });

  const showNotification = (notificationType, message, title, width) => {
    let colorCode = notificationType === 'success' ? 'secondary.0' : 'red';

    notifications.show({
      id: 'lanlord-property-management-notification',
      withCloseButton: true,
      title: title,
      message: message,
      autoClose: 3000,
      color: colorCode,
      icon:
        notificationType === 'success' ? (
          <IconCheck size="2.5rem" />
        ) : (
          <IconX size="2.5rem" />
        ),
      style: {
        backgroundColor: theme.colors.background[0],
        padding: '12px 12px',
        fontSize: '8rem',
        lineHeight: '1.2',
        minHeight: '120px',
        width: width,
        maxWidth: '90%'
      },
      styles: {
        title: {
          fontSize: '1.5rem',
          lineHeight: 1.3
        },
        description: {
          fontSize: '1.2rem',
          lineHeight: 1.4
        },
        closeButton: {
          color: 'white',
          backgroundColor: 'black',
          '&:hover': {
            backgroundColor: 'gray',
            color: 'yellow'
          }
        }
      }
    });
  };

  const propertyOptions = properties.map((prop) => ({
    value: prop.propertyId,
    label: prop.propertName
  }));

  return (
    <>
      <Box className={classes.wrapper}>
        <Box className={classes.headerAndSearch}>
          <p className={classes.title}>{data.titles.propertyList}</p>
          <Box className={classes.searchAndFilter}>
            <TextInput
              icon={<SearchIcon />}
              onChange={(e) =>
                handleSearch(e, pageLoadEntries, setEntries, [
                  'name',
                  'email',
                  'propertName',
                  'access'
                ])
              }
              radius={'xl'}
              className={classes.searchInput}
              placeholder={data.titles.search}
            />
            {userType === propertiesUserType.landlord ? (
              <MediaQuery
                query="(max-width: 500px)"
                styles={{ display: 'none' }}
              >
                <Button
                  onClick={() => setViewAddManager(true)}
                  color="primary.0"
                  variant="white"
                  radius="16px"
                  size="lg"
                >
                  <img
                    className={classes.plusIcon}
                    src={plusIcon}
                    alt="plus-icon"
                  />
                  {data.cta.addProperty}
                </Button>
              </MediaQuery>
            ) : (
              ''
            )}
          </Box>
          {userType === propertiesUserType.landlord ? (
            <MediaQuery query="(min-width: 500px)" styles={{ display: 'none' }}>
              <Button
                onClick={() => setViewAddManager(true)}
                color="primary.0"
                variant="white"
                radius="16px"
                size="lg"
                fullWidth={true}
              >
                <img
                  className={classes.plusIcon}
                  src={plusIcon}
                  alt="plus-icon"
                />
                {data.cta.addProperty}
              </Button>
            </MediaQuery>
          ) : (
            ''
          )}
        </Box>
        <Box className={classes.tableContainer}>
          <Table className={classes.table}>
            <thead className={classes.tableHead}>
              <tr>
                {data.tableHeading[userType.toLowerCase()].map(
                  (heading, idx) => {
                    return (
                      <th key={idx} className={classes.columnHeading}>
                        {heading}
                      </th>
                    );
                  }
                )}
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </Table>
        </Box>
        {rows.length === 0 ? (
          <p className="no-record-found">{noRecordFound}</p>
        ) : (
          <Pagination
            size={'md'}
            value={activePage}
            onChange={pageChangeHandler}
            className={classes.pagination}
            siblings={1}
            total={totalPage}
          />
        )}
      </Box>
      <Modal
        opened={viewAddManager}
        onClose={() => setViewAddManager(false)}
        title={editManager ? 'Update Property Manager' : 'Add Property Manager'}
        size="lg"
        centered
        radius="md"
        overlayProps={{ blur: 3, opacity: 0.5 }}
      >
        {/* Modal content remains the same */}
        <Stack spacing="md">
          {!editManager && (
            <Grid>
              <Col span={6}>
                <TextInput
                  label="Manager Name"
                  placeholder="Enter full name"
                  radius="lg"
                  className={classes.textInput}
                  value={mangerform.values.propertyManagerName}
                  onChange={(e) =>
                    mangerform.setFieldValue(
                      'propertyManagerName',
                      e.currentTarget.value
                    )
                  }
                />
              </Col>
              <Col span={6}>
                <TextInput
                  label="Manager Email"
                  placeholder="Enter email"
                  radius="lg"
                  className={classes.textInput}
                  value={mangerform.values.propertyManagerEmail}
                  onChange={(e) =>
                    mangerform.setFieldValue(
                      'propertyManagerEmail',
                      e.currentTarget.value
                    )
                  }
                />
              </Col>
            </Grid>
          )}
          <Grid>
            <Col span={12}>
              <MultiSelect
                label="Properties"
                placeholder="Select one or more properties"
                radius="lg"
                data={propertyOptions}
                value={mangerform.values.propertyIds}
                onChange={(value) =>
                  mangerform.setFieldValue('propertyIds', value)
                }
                searchable
                clearable
                withinPortal
                styles={(theme) => ({
                  input: {
                    minHeight: '48px',
                    padding: '8px 12px',
                    fontSize: '14px',
                    borderRadius: '12px',
                    border: `2px solid ${theme.colors.gray[3]}`,
                    '&:focus': {
                      borderColor: theme.colors.primary[0]
                    }
                  },
                  label: {
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '8px',
                    color: theme.colors.gray[7]
                  },
                  value: {
                    backgroundColor: theme.colors.primary[0],
                    color: 'white',
                    borderRadius: '8px',
                    padding: '4px 8px',
                    fontSize: '13px'
                  },
                  defaultValueRemove: {
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.colors.primary[1]
                    }
                  },
                  searchInput: {
                    fontSize: '14px'
                  },
                  dropdown: {
                    borderRadius: '12px',
                    border: `1px solid ${theme.colors.gray[3]}`,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  },
                  item: {
                    fontSize: '14px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    '&[data-selected]': {
                      backgroundColor: theme.colors.primary[0],
                      color: 'white'
                    },
                    '&[data-hovered]': {
                      backgroundColor: theme.colors.gray[1]
                    }
                  }
                })}
              />
              {editManager && (
                <Text size="xs" color="dimmed" mt={8}>
                  You can reassign this manager or add them to additional
                  properties
                </Text>
              )}
            </Col>
            <Col span={12}>
              <Box>
                <Text size="sm" weight={500} mb={8} color="gray.7">
                  Access Permissions
                </Text>
                <Stack spacing={8}>
                  {[
                    { label: 'Service Request', value: 'service-request' },
                    { label: 'Read Only', value: 'Read Only' },
                    { label: 'Invoice', value: 'invoice' },
                    { label: 'Full Access', value: 'full-access' }
                  ].map(({ label, value }) => (
                    <Checkbox
                      key={value}
                      label={label}
                      value={value}
                      size="md"
                      checked={mangerform.values.propertyManagerAccess?.includes(
                        value
                      )}
                      onChange={(event) => {
                        const isChecked = event.currentTarget.checked;
                        const current =
                          mangerform.values.propertyManagerAccess || [];

                        let updatedAccess = [...current];

                        if (value === 'full-access') {
                          updatedAccess = isChecked ? ['full-access'] : [];
                        } else {
                          updatedAccess = updatedAccess.filter(
                            (v) => v !== 'full-access'
                          );

                          if (value === 'invoice' && isChecked) {
                            updatedAccess = updatedAccess.filter(
                              (v) => v !== 'Read Only'
                            );
                          }

                          if (value === 'Read Only' && isChecked) {
                            updatedAccess = updatedAccess.filter(
                              (v) => v !== 'invoice'
                            );
                          }

                          if (isChecked) {
                            updatedAccess.push(value);
                          } else {
                            updatedAccess = updatedAccess.filter(
                              (v) => v !== value
                            );
                          }
                        }

                        mangerform.setFieldValue(
                          'propertyManagerAccess',
                          updatedAccess
                        );
                      }}
                      styles={(theme) => ({
                        root: {
                          padding: '12px',
                          borderRadius: '8px',
                          border: `1px solid ${theme.colors.gray[2]}`,
                          backgroundColor: 'white',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: theme.colors.gray[0],
                            borderColor: theme.colors.gray[3]
                          }
                        },
                        label: {
                          fontSize: '14px',
                          fontWeight: 400,
                          paddingLeft: '8px'
                        },
                        input: {
                          cursor: 'pointer',
                          '&:checked': {
                            backgroundColor: theme.colors.primary[0],
                            borderColor: theme.colors.primary[0]
                          }
                        }
                      })}
                    />
                  ))}
                </Stack>
              </Box>
            </Col>
          </Grid>

          <Divider my="sm" />

          <Group position="right">
            <Button
              variant="light"
              color="red"
              onClick={() => setViewAddManager(false)}
            >
              Cancel
            </Button>

            <Button
              onClick={handleAddManager}
              disabled={
                sending ||
                !mangerform.isValid() ||
                mangerform.getInputProps('propertyManagerAccess').value === null
              }
              styles={(theme) => ({
                root: {
                  backgroundColor: theme.colors.primary[0],
                  color: theme.white,
                  '&:hover': {
                    backgroundColor: theme.colors.primary[1]
                  }
                }
              })}
            >
              {editManager
                ? sending
                  ? 'Updating...'
                  : 'Update Manager'
                : sending
                  ? 'Adding...'
                  : 'Add Manager'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default PropertyManagementList;
